import {
    type AccountRegisterRouteResponse,
    mergeCartNotificationsPreservingValidateOnLoad,
    PageType,
    persistPendingAddressSaveRetry,
    type RefetchCartStateOptions,
} from '@libs/common-next';
import type { CartUI } from '@libs/common-node';

import { CART_PAGE, CHECKOUT_PAGE } from '../SignInForm/constants';
import { resolvePostSignInCartMergeNavigation } from '../SignInForm/resolvePostSignInCartMergeNavigation';
import {
    notificationsPreservedFromAuth,
    type ValidateCartMergeArgs,
    type ValidateCartMergeResult,
} from '../SignInForm/validateCartMerge';

type Auth0MergeResponseCart = {
    id?: string;
    merged?: boolean;
    notifications?: CartUI['notifications'];
};

function getAuth0MergeResponseCart(mergeRes: AccountRegisterRouteResponse): Auth0MergeResponseCart | undefined {
    return mergeRes.data?.updatedCart ?? mergeRes.data?.cart;
}

function cartWithPreservedAuth0MergeNotifications(
    refetchedCart: CartUI | null,
    mergeResponseCart: Auth0MergeResponseCart | undefined
): CartUI | null {
    if (!refetchedCart || !mergeResponseCart) {
        return refetchedCart;
    }

    const preservedNotifications = notificationsPreservedFromAuth(
        mergeResponseCart.notifications,
        refetchedCart.notifications
    );

    return {
        ...refetchedCart,
        ...(mergeResponseCart.merged ? { merged: true } : {}),
        ...(preservedNotifications.length ||
        mergeResponseCart.notifications != null ||
        refetchedCart.notifications != null
            ? { notifications: preservedNotifications }
            : {}),
    };
}

export type RunAuth0CartMergeArgs = {
    isAuth0AccessToken: boolean;
    accessToken: string;
    isCartReady: boolean;
    hasCompletedMerge: boolean;
    pageType: PageType;
    isCheckoutLogin: boolean;
    guestCartLineItemsSignature: string | null;
    mergeGuestCart: () => Promise<AccountRegisterRouteResponse | null | undefined>;
    refetchCart: (options?: RefetchCartStateOptions) => Promise<CartUI | null>;
    validateMergedCart: (cart: CartUI | null) => Promise<ValidateCartMergeResult>;
    shouldRedirectToCheckout: (cart: CartUI) => boolean;
    setCart: ValidateCartMergeArgs['setCart'];
    setNotifications: ValidateCartMergeArgs['setNotifications'];
    onMarkCompleted: () => void;
    onSetCartCookie: (cartId: string) => void;
    onSetPostMergePath: (path: string) => void;
    onClearProceedToCheckout: () => void;
};

function commitAuth0MergedCart(cart: CartUI, args: Pick<RunAuth0CartMergeArgs, 'setCart' | 'setNotifications'>) {
    args.setCart(cart);

    if (cart.notifications?.length) {
        args.setNotifications(prev => mergeCartNotificationsPreservingValidateOnLoad(prev, cart.notifications));
    }
}

let inFlightMerge: Promise<'not_ready' | 'done'> | null = null;

export function resetAuth0CartMergeInFlight() {
    inFlightMerge = null;
}

export function resolveAuth0MergePageType(destinationPath: string, fallback: PageType): PageType {
    if (destinationPath.includes('/checkout')) {
        return PageType.CHECKOUT;
    }
    if (destinationPath.includes('/cart')) {
        return PageType.CART;
    }
    return fallback;
}

export function isAuth0CheckoutLoginDestination(destinationPath: string, pathname?: string | null): boolean {
    return destinationPath.includes('/checkout') || Boolean(pathname?.includes('checkout-login'));
}

function isCartOrCheckout(pageType: PageType): boolean {
    return pageType === PageType.CART || pageType === PageType.CHECKOUT;
}

async function applyAuth0PostMergeNavigation(
    args: RunAuth0CartMergeArgs,
    updatedCart: CartUI | null,
    isCartMergeSuccessful: boolean
) {
    if (!isCartMergeSuccessful) {
        args.onSetPostMergePath(CART_PAGE);
        return;
    }

    const decision = resolvePostSignInCartMergeNavigation({
        pageType: args.pageType,
        isCheckoutLogin: args.isCheckoutLogin,
        updatedCart,
        guestCartLineItemsSignature: args.guestCartLineItemsSignature,
        shouldRedirectToCheckout: args.shouldRedirectToCheckout,
    });

    if (decision.kind === 'clear_proceed_and_stay') {
        args.onClearProceedToCheckout();
        return;
    }

    if (decision.kind === 'push') {
        if (decision.path === CHECKOUT_PAGE && updatedCart?.merged) {
            args.setCart({ ...updatedCart, merged: false });
        }
        args.onSetPostMergePath(decision.path);
    }
}

function isAuth0CartMergeReady(args: RunAuth0CartMergeArgs): boolean {
    return args.isAuth0AccessToken && Boolean(args.accessToken) && args.isCartReady;
}

async function executeAuth0CartMerge(args: RunAuth0CartMergeArgs): Promise<'not_ready' | 'done'> {
    if (!isAuth0CartMergeReady(args)) {
        return 'not_ready';
    }

    if (args.hasCompletedMerge) {
        return 'done';
    }

    args.onMarkCompleted();

    const mergeRes = await args.mergeGuestCart();
    if (!mergeRes?.success) {
        return 'done';
    }

    const mergeResponseCart = getAuth0MergeResponseCart(mergeRes);
    const hydratedCartId = mergeResponseCart?.id;
    if (hydratedCartId) {
        args.onSetCartCookie(hydratedCartId);
    }

    // Checkout reads this when the merged cart reaches cart state, so it must be stored before `refetchCart`.
    persistPendingAddressSaveRetry(hydratedCartId, mergeRes.data?.deliveryInstructionsSaveFailed);

    const refetchedCart = await args.refetchCart(hydratedCartId ? { cartId: hydratedCartId } : undefined);
    const updatedCart = cartWithPreservedAuth0MergeNotifications(refetchedCart, mergeResponseCart);

    // refetchCart already published the GET cart without merge banners; write them before /validate
    // (including 303 `{ size, timeout }`, 422 recovery, and off cart/checkout where validate is skipped).
    if (updatedCart && (updatedCart.merged || updatedCart.notifications?.length)) {
        commitAuth0MergedCart(updatedCart, args);
    }

    if (!isCartOrCheckout(args.pageType)) {
        return 'done';
    }

    const cartMergeValidation = await args.validateMergedCart(updatedCart);
    await applyAuth0PostMergeNavigation(args, cartMergeValidation.cart ?? updatedCart, cartMergeValidation.success);
    return 'done';
}

export async function runAuth0CartMerge(args: RunAuth0CartMergeArgs): Promise<'not_ready' | 'done'> {
    if (!isAuth0CartMergeReady(args)) {
        return 'not_ready';
    }

    if (inFlightMerge) {
        return inFlightMerge;
    }

    inFlightMerge = executeAuth0CartMerge(args);
    try {
        return await inFlightMerge;
    } finally {
        inFlightMerge = null;
    }
}
